import { addDays, format, parseISO, startOfDay } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCurrentUser } from '@/hooks';
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Typography,
} from '@/ui';
import { normalizeMemberBookingSlots } from '@/utils';

import {
  legacyAppSelectedPair,
  matchesSelectedPairContext,
} from './momentsSelectedPairContract';
import { toLegacyMemberBookingContext } from './schedulingContextAdapter';
import {
  PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE,
  PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE,
  usePlatformAdminAppointmentLifecycleGuard,
} from './usePlatformAdminAppointmentLifecycleGuard';

import type { MomentsSelectedPairSchedulingContext } from './momentsSelectedPairContract';
import type {
  AppointmentKind,
  PlatformAdminAppointment,
  PlatformAdminCalendarConflict,
} from './types';
import type {
  AdminLifecycleCommand,
  PlatformAdminLifecycleGuardOptions,
} from './usePlatformAdminAppointmentLifecycleGuard';
import type { FC } from 'react';

import { MentoCalBookingDetailRow } from '@/components/MentoCal/BookingDetailRow';
import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import {
  mentoCalBookingDayHeading,
  MentoCalTimeList,
} from '@/components/MentoCal/TimeList';
import { MentoCalTimeSlotButton } from '@/components/MentoCal/TimeSlotButton';
import { MENTO_LOGO_URL } from '@/components/MentoCal/constants';
import { seriesLengthLabel } from '@/components/MentoCal/seriesLength';
import {
  useGetMomentsSelectedPairSchedulingContextQuery,
  useLazyGetMomentsSelectedPairSchedulingContextQuery,
} from '@/store/api/momentsAppointmentApi';

interface PlatformAdminBookingPageProps {
  bookingType?: 'biweekly' | 'chemistry' | 'single';
  coachId: string;
  kind: AppointmentKind;
  lifecycleOptions?: PlatformAdminLifecycleGuardOptions;
  memberId: string;
  onDone: () => void;
}

interface BookingWindowCache {
  context?: MomentsSelectedPairSchedulingContext;
  days: Record<
    string,
    {
      day: MomentsSelectedPairSchedulingContext['availability']['days'][number];
      owner: string;
    }
  >;
  latestEnd: string;
}

const appendBookingWindow = (
  previous: BookingWindowCache,
  response: MomentsSelectedPairSchedulingContext,
): BookingWindowCache => {
  const days = { ...previous.days };
  const owner = response.bookingWindow!.startDate;
  response.availability.days.forEach((day) => {
    days[day.date] = { day, owner };
  });
  const end = response.availability.days.at(-1)?.date ?? '';
  return {
    context: response,
    days,
    latestEnd: end > previous.latestEnd ? end : previous.latestEnd,
  };
};

const viewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const requiresCalendarConflictOverride = (
  conflict: PlatformAdminCalendarConflict | null | undefined,
): boolean =>
  // Legacy contexts have no policy flags and required confirmation for every conflict.
  conflict !== null &&
  conflict !== undefined &&
  conflict.overrideRequired !== false;

const formatConfirmedSchedule = (
  appointment: PlatformAdminAppointment,
): string => {
  const { endAt, startAt, timezone } = appointment.currentSchedule;
  const start = new Date(startAt);
  const end = new Date(endAt);
  const date = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
  }).format(start);
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
  const timezoneName =
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(start)
      .find((part) => part.type === 'timeZoneName')?.value ?? timezone;

  return `${date} ${timeFormatter.format(start)} - ${timeFormatter.format(end)} ${timezoneName}`;
};

/**
 * MEN-3990: the Figma left rail labels the zone "PST", not
 * "America/Los_Angeles". Falls back to the IANA name if the runtime cannot
 * produce a short name.
 */
const shortTimezoneLabel = (timezone: string, onDate: string): string => {
  // The abbreviation is DST-dependent, so it has to be resolved against the day
  // being booked rather than today: an admin arranging a January session in
  // July would otherwise be told those times are PDT. Midday UTC keeps the
  // lookup clear of the ~2am local transition in either direction.
  const instant = new Date(`${onDate}T12:00:00Z`);
  if (Number.isNaN(instant.valueOf())) return timezone;
  try {
    return (
      new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      })
        .formatToParts(instant)
        .find((part) => part.type === 'timeZoneName')?.value ?? timezone
    );
  } catch {
    return timezone;
  }
};

/**
 * MEN-3990: the Figma left rail carries a description under the heading. Its
 * copy is written for the member ("Jason becomes your coach"), but this page
 * refuses to run when the actor is the member, so the reader is always Ops or
 * the coach (MEN-5635) booking on the member's behalf — hence third person.
 *
 * The wording tracks the booking type: only the recurring variant actually
 * establishes a coaching relationship, so chemistry and single-session say what
 * they really are instead.
 */
const bookingDescription = (
  bookingType: 'biweekly' | 'chemistry' | 'single',
  coachName: string,
  memberName: string,
): string => {
  if (bookingType === 'chemistry') {
    return `Booking for ${memberName}. Pick a time for their chemistry call with ${coachName}.`;
  }
  if (bookingType === 'single') {
    return `Booking for ${memberName}. Pick a time for their coaching session with ${coachName}.`;
  }
  return `Booking for ${memberName}. Pick a time, and ${coachName} becomes their coach.`;
};

const PlatformAdminBookingForm: FC<PlatformAdminBookingPageProps> = ({
  bookingType,
  coachId,
  kind,
  lifecycleOptions,
  memberId,
  onDone,
}: PlatformAdminBookingPageProps) => {
  const productBookingType =
    bookingType ?? (kind === 'chemistry' ? 'chemistry' : 'biweekly');
  const { user, isUserLoading } = useCurrentUser();
  const timezone = useMemo(viewerTimezone, []);
  const [bookingWindowStart, setBookingWindowStart] = useState(() =>
    format(new Date(), 'yyyy-MM-dd'),
  );
  const [today, setToday] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  useEffect(() => {
    const updateToday = () => setToday(format(new Date(), 'yyyy-MM-dd'));
    const timeout = window.setTimeout(
      updateToday,
      addDays(startOfDay(new Date()), 1).getTime() - Date.now(),
    );
    window.addEventListener('focus', updateToday);
    document.addEventListener('visibilitychange', updateToday);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener('focus', updateToday);
      document.removeEventListener('visibilitychange', updateToday);
    };
  }, [today]);
  const visibleWeeks = kind === 'chemistry' ? 2 : 1;
  const [windowCache, setWindowCache] = useState<BookingWindowCache>({
    days: {},
    latestEnd: '',
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStart, setSelectedStart] = useState('');
  const [selectionContext, setSelectionContext] =
    useState<MomentsSelectedPairSchedulingContext>();
  const [selectionPending, setSelectionPending] = useState(false);
  const [selectionFailed, setSelectionFailed] = useState(false);
  const selectionGeneration = useRef(0);
  const [refreshWindow, selectionQuery] =
    useLazyGetMomentsSelectedPairSchedulingContextQuery();
  const selectedSlotSnapshot = useRef('');
  useEffect(
    () => () => {
      selectionGeneration.current += 1;
    },
    [],
  );
  const [confirmedConflictFingerprint, setConfirmedConflictFingerprint] =
    useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    window.crypto.randomUUID(),
  );
  const selectedPair = useMemo(
    () => legacyAppSelectedPair(coachId, memberId),
    [coachId, memberId],
  );
  const contextRequest = {
    ...selectedPair,
    ...(kind === 'coaching' ? { bookingWindowStart } : {}),
    ...(productBookingType === 'single'
      ? { bookingPattern: 'one_off' as const }
      : {}),
    intent: 'book' as const,
    kind,
    viewerTimezone: timezone,
    visibleWeeks,
  };
  const context =
    useGetMomentsSelectedPairSchedulingContextQuery(contextRequest);
  const retainedContext =
    (kind === 'coaching'
      ? matchesSelectedPairContext(context.data, selectedPair)
      : context.data?.coach.id === coachId &&
        context.data.member.id === memberId) && context.data?.kind === kind
      ? context.data
      : undefined;
  const pageContext = context.currentData ?? retainedContext;
  const loadedPage =
    kind === 'coaching' &&
    !context.isFetching &&
    !context.isError &&
    pageContext?.bookingWindow?.startDate === bookingWindowStart &&
    matchesSelectedPairContext(pageContext, selectedPair) &&
    pageContext.kind === kind &&
    pageContext.timezone === timezone
      ? pageContext
      : null;
  const loadedSelection =
    kind === 'coaching' &&
    selectionContext &&
    !selectionPending &&
    !selectionQuery.isFetching &&
    !selectionQuery.isError &&
    selectionQuery.currentData?.bookingWindow?.startDate ===
      selectionContext.bookingWindow?.startDate &&
    matchesSelectedPairContext(selectionQuery.currentData, selectedPair)
      ? selectionQuery.currentData
      : null;
  // Independent delivery fingerprints preserve last-response-wins across overlaps.
  const windowResponse = loadedPage ? JSON.stringify(loadedPage) : '';
  const selectionResponse = loadedSelection
    ? JSON.stringify(loadedSelection)
    : '';
  useEffect(() => {
    if (!windowResponse) return;
    const response: MomentsSelectedPairSchedulingContext =
      JSON.parse(windowResponse);
    setWindowCache((previous) => appendBookingWindow(previous, response));
  }, [windowResponse]);
  useEffect(() => {
    if (!selectionResponse) return;
    const response: MomentsSelectedPairSchedulingContext =
      JSON.parse(selectionResponse);
    setWindowCache((previous) => appendBookingWindow(previous, response));
  }, [selectionResponse]);
  const schedulingContext =
    kind === 'coaching'
      ? ((selectionContext &&
        selectionQuery.currentData?.bookingWindow?.startDate ===
          selectionContext.bookingWindow?.startDate &&
        matchesSelectedPairContext(selectionQuery.currentData, selectedPair)
          ? selectionQuery.currentData
          : selectionContext) ??
        pageContext ??
        windowCache.context)
      : pageContext;
  const navigationDays =
    kind === 'coaching'
      ? Object.values(windowCache.days)
          .map(({ day }) => day)
          .sort((a, b) => a.date.localeCompare(b.date))
      : (schedulingContext?.availability.days ?? []);
  const visibleDays = navigationDays.filter((day) => day.date >= today);
  const legacy = schedulingContext
    ? toLegacyMemberBookingContext({
        ...schedulingContext,
        availability: { days: navigationDays },
      })
    : undefined;
  const selectedCommandDay = schedulingContext?.availability.days.find(
    (day) => day.date === selectedDate,
  );
  const selectedFormConflict = selectedCommandDay?.slots.find(
    (slot) => slot.startAt === selectedStart,
  )?.calendarConflict;
  const selectedFormConflictFingerprint = selectedFormConflict
    ? `${selectedStart}:${JSON.stringify(selectedFormConflict)}`
    : '';
  const currentSlotFingerprint = JSON.stringify(
    selectedCommandDay?.slots.find((slot) => slot.startAt === selectedStart) ??
      null,
  );
  const command: AdminLifecycleCommand = {
    coachId,
    command: 'book',
    idempotencyKey,
    kind,
    memberId,
    payload: {
      ...(productBookingType === 'single'
        ? { bookingPattern: { type: 'one_off' as const } }
        : {}),
      calendarConflictOverride:
        requiresCalendarConflictOverride(selectedFormConflict) &&
        selectedFormConflictFingerprint !== '' &&
        confirmedConflictFingerprint === selectedFormConflictFingerprint,
      recurrence:
        productBookingType === 'biweekly' &&
        selectedCommandDay?.appointmentCount
          ? {
              appointmentCount: selectedCommandDay.appointmentCount,
              cadenceWeeks: schedulingContext?.cadenceWeeks ?? 2,
            }
          : null,
      seatType: schedulingContext?.seatType ?? null,
      startAt: selectedStart,
      viewerTimezone: timezone,
    },
    presentation: {
      memberEmail: schedulingContext?.member.email ?? '',
    },
  };
  const lifecycle = usePlatformAdminAppointmentLifecycleGuard(
    command,
    lifecycleOptions,
  );
  const storedBookCommand =
    lifecycle.record && lifecycle.command.command === 'book'
      ? lifecycle.command
      : null;
  const storedDay = legacy?.availability.days.find((day) =>
    day.slots.some(
      (slot) => slot.startTime === storedBookCommand?.payload.startAt,
    ),
  );
  const displayedDate = storedDay?.date ?? selectedDate;
  // MEN-3990: which day the rail's timezone abbreviation is resolved against.
  //
  // Availability carries unbookable days too — disabled ones, and enabled ones
  // with no remaining slots — so the first entry is not necessarily a day
  // anything can be booked on. Across a daylight-saving boundary that matters:
  // a disabled October 31 would report EDT for a range whose only selectable
  // days are in November. Anchor on the first day actually on offer, and fall
  // back to the stable IANA name when there is none.
  const timezoneAnchorDate =
    displayedDate ||
    (legacy?.availability.days.find(
      (day) => day.enabled && day.slots.length > 0,
    )?.date ??
      '');
  const displayedStart = storedBookCommand?.payload.startAt ?? selectedStart;
  const displayedStartAvailable =
    schedulingContext?.availability.days.some((day) =>
      day.slots.some((slot) => slot.startAt === displayedStart),
    ) ?? false;
  const selectionLegacy = schedulingContext
    ? toLegacyMemberBookingContext(schedulingContext)
    : undefined;
  const selectedDay = (
    storedBookCommand ? legacy : selectionLegacy
  )?.availability.days.find(({ date }) => date === displayedDate);
  const slots = normalizeMemberBookingSlots(selectedDay?.slots ?? []);
  // MEN-5390: the series length the rail shows is the one Ops will book — the
  // stored command's recurrence while a booking is in flight, otherwise the
  // selected day's count that `command` submits — falling back like the
  // member rail does before a date is picked.
  const seriesSessionCount =
    storedBookCommand?.payload.recurrence?.appointmentCount ??
    selectedCommandDay?.appointmentCount ??
    schedulingContext?.appointmentCount ??
    schedulingContext?.availability.days.find(
      (day) => day.appointmentCount !== null,
    )?.appointmentCount;
  const selectedConflict = schedulingContext?.availability.days
    .flatMap((day) => day.slots)
    .find((slot) => slot.startAt === displayedStart)?.calendarConflict;
  const selectedConflictFingerprint = selectedConflict
    ? `${displayedStart}:${JSON.stringify(selectedConflict)}`
    : '';
  const conflictConfirmed =
    selectedConflictFingerprint !== '' &&
    confirmedConflictFingerprint === selectedConflictFingerprint;
  const conflictOverrideRequired =
    requiresCalendarConflictOverride(selectedConflict);
  let bookingButtonLabel = 'Book';
  if (lifecycle.record?.phase === 'ambiguous')
    bookingButtonLabel = 'Retry booking';
  else if (conflictOverrideRequired && !conflictConfirmed)
    bookingButtonLabel = 'Confirm conflict';
  const adminNonterminal =
    lifecycle.record?.phase === 'submitting' ||
    lifecycle.record?.phase === 'ambiguous' ||
    lifecycle.record?.phase === 'pending';
  const adminCommandFrozen =
    adminNonterminal ||
    lifecycle.record?.phase === 'needs_reconciliation' ||
    lifecycle.record?.phase === 'rejected';
  useEffect(() => {
    if (
      adminCommandFrozen ||
      lifecycle.record ||
      !selectedStart ||
      selectionPending ||
      !selectedSlotSnapshot.current ||
      selectedSlotSnapshot.current === currentSlotFingerprint
    )
      return;
    setSelectedStart('');
    setConfirmedConflictFingerprint('');
    selectedSlotSnapshot.current = '';
  }, [
    adminCommandFrozen,
    lifecycle.record,
    selectedStart,
    selectionPending,
    currentSlotFingerprint,
  ]);
  useEffect(() => {
    if (adminCommandFrozen || lifecycle.record) return;
    if (selectedDate && selectedDate < today) {
      selectionGeneration.current += 1;
      setSelectedDate('');
      setSelectedStart('');
      setSelectionContext(undefined);
      setSelectionPending(false);
      setSelectionFailed(false);
      setConfirmedConflictFingerprint('');
    }
    if (bookingWindowStart < today) setBookingWindowStart(today);
  }, [
    adminCommandFrozen,
    lifecycle.record,
    selectedDate,
    today,
    bookingWindowStart,
  ]);
  const eligible = schedulingContext?.eligibility.eligible ?? false;
  const eligibilityReasons =
    schedulingContext?.eligibility.reasons.map((reason) =>
      reason.replaceAll('_', ' '),
    ) ?? [];
  const newBookingIneligible = !eligible && !lifecycle.record;
  const availabilityRefreshFailed =
    (context.isError || selectionFailed || selectionQuery.isError) &&
    Boolean(legacy);
  const actorIsMember = !isUserLoading && user.id === memberId;
  let bookingRegionLabel = 'Book bi-weekly coaching';
  let bookingHeading = `Book ${legacy?.seatType === 'executive' ? 'Bi-Weekly Executive Coaching' : 'Bi-Weekly Leadership Coaching'}`;
  if (productBookingType === 'chemistry') {
    bookingRegionLabel = 'Book a chemistry call';
    bookingHeading = bookingRegionLabel;
  } else if (productBookingType === 'single') {
    bookingRegionLabel = 'Book a single coaching session';
    bookingHeading = bookingRegionLabel;
  }
  const confirmed =
    lifecycle.result?.status === 'confirmed'
      ? lifecycle.result.appointments[0]
      : undefined;

  const selectCoachingDate = (date: string) => {
    if (adminCommandFrozen) return;
    const cachedOwner = windowCache.days[date]?.owner;
    if (!cachedOwner || date < today) return;
    const owner = cachedOwner < today ? today : cachedOwner;
    selectionGeneration.current += 1;
    const generation = selectionGeneration.current;
    setSelectionPending(true);
    setSelectionFailed(false);
    setSelectedDate(date);
    if (date !== selectedDate) {
      setSelectedStart('');
      setConfirmedConflictFingerprint('');
    }
    const request = refreshWindow(
      { ...contextRequest, bookingWindowStart: owner },
      false,
    );
    request
      .unwrap()
      .then((response) => {
        if (generation !== selectionGeneration.current) return;
        if (
          response.bookingWindow?.startDate !== owner ||
          response.kind !== kind ||
          response.timezone !== timezone ||
          !matchesSelectedPairContext(response, selectedPair)
        )
          throw new Error('Mismatched selection window');
        const refreshedDay = response.availability.days.find(
          (day) => day.date === date,
        );
        const oldSlot = selectedCommandDay?.slots.find(
          (slot) => slot.startAt === selectedStart,
        );
        const newSlot = refreshedDay?.slots.find(
          (slot) => slot.startAt === selectedStart,
        );
        if (!newSlot || JSON.stringify(newSlot) !== JSON.stringify(oldSlot)) {
          setSelectedStart('');
          setConfirmedConflictFingerprint('');
        }
        setSelectionContext(response);
        setWindowCache((previous) => appendBookingWindow(previous, response));
        setSelectionPending(false);
      })
      .catch(() => {
        if (generation !== selectionGeneration.current) return;
        setSelectionPending(false);
        setSelectionFailed(true);
      });
  };

  if (isUserLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200">
        <CircularProgress size={20} />
      </main>
    );
  }

  if (confirmed) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-100 px-4">
        <Paper className="w-full max-w-lg p-8 text-center" elevation={3}>
          <Typography variant="h5">
            {productBookingType === 'chemistry'
              ? 'Chemistry call booked!'
              : `Coaching with ${confirmed.coach.displayName.split(' ')[0]} is set!`}
          </Typography>
          <Typography className="mt-3" variant="h6">
            {confirmed.coach.displayName}
          </Typography>
          <Typography>Booked for {confirmed.member.displayName}</Typography>
          <Typography className="mt-4">
            {formatConfirmedSchedule(confirmed)}
          </Typography>
          <Typography className="mt-3" color="text.secondary">
            Invite sent to {lifecycle.command.presentation.memberEmail}
          </Typography>
          <Button
            className="mt-6"
            variant="contained"
            onClick={() => {
              lifecycle.consumeResult();
              onDone();
            }}
          >
            Done
          </Button>
        </Paper>
      </main>
    );
  }

  if (context.isLoading || (context.isFetching && !legacy)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200">
        <CircularProgress size={20} />
      </main>
    );
  }

  if (!legacy) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200">
        <Alert severity="error">Unable to load booking details.</Alert>
      </main>
    );
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center gap-12 bg-sunny-100 px-4 py-8">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={onDone}
      >
        <Icon>close</Icon>
      </IconButton>
      <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
      <Paper
        aria-label={bookingRegionLabel}
        className="flex w-full max-w-[1141px] flex-col overflow-hidden lg:flex-row"
        elevation={3}
        role="region"
      >
        <section className="flex w-full flex-col gap-4 border-r border-solid border-cement-200 p-8 lg:w-[379px]">
          <div className="flex items-center gap-3">
            <Avatar
              firstName={legacy.coach.fullName}
              lastName=""
              size="s"
              src={legacy.coach.avatarUrl ?? undefined}
            />
            <Typography className="font-bold">
              {legacy.coach.fullName}
            </Typography>
          </div>
          <Typography variant="h5">{bookingHeading}</Typography>
          {legacy.coach.fullName && legacy.member.fullName ? (
            <Typography className="text-cement-400">
              {bookingDescription(
                productBookingType,
                legacy.coach.fullName,
                legacy.member.fullName,
              )}
            </Typography>
          ) : null}
          <div className="flex flex-col gap-4">
            <MentoCalBookingDetailRow icon="account_box">
              {legacy.member.email}
            </MentoCalBookingDetailRow>
            {productBookingType === 'biweekly' &&
            schedulingContext?.cadenceWeeks ? (
              <MentoCalBookingDetailRow icon="calendar_month">
                Every {schedulingContext.cadenceWeeks} weeks
              </MentoCalBookingDetailRow>
            ) : null}
            <MentoCalBookingDetailRow icon="schedule">
              {legacy.durationMinutes} Minutes
            </MentoCalBookingDetailRow>
            {productBookingType === 'biweekly' &&
            schedulingContext?.cadenceWeeks &&
            seriesSessionCount ? (
              <MentoCalBookingDetailRow icon="event_repeat">
                {seriesLengthLabel(
                  seriesSessionCount,
                  schedulingContext.cadenceWeeks,
                )}
              </MentoCalBookingDetailRow>
            ) : null}
            <MentoCalBookingDetailRow icon="public">
              {shortTimezoneLabel(legacy.timezone, timezoneAnchorDate)}
            </MentoCalBookingDetailRow>
          </div>
        </section>
        <section className="w-full border-r border-solid border-cement-200 p-8 lg:w-[407px]">
          <MentoCalDatePicker
            canShowMore={
              kind === 'coaching' &&
              !adminCommandFrozen &&
              !availabilityRefreshFailed
            }
            days={visibleDays.map(({ date, enabled }) => ({
              date,
              enabled:
                enabled &&
                !adminCommandFrozen &&
                (kind === 'coaching' || eligible),
            }))}
            isLoadingMore={context.isFetching}
            selectedDate={displayedDate}
            // MEN-3990: the Figma calendar is the roomy variant — 44px cells,
            // 4px gaps, SUN/MON headers, and a sage-100 fill on selectable
            // days. Without this the picker falls back to its compact form,
            // where selectable days render plain white and read the same as
            // unavailable ones.
            comfortable
            onSelectDate={(date) => {
              if (adminCommandFrozen) return;
              if (kind === 'coaching') {
                selectCoachingDate(date);
                return;
              }
              setSelectedDate(date);
              setSelectedStart('');
            }}
            onShowMore={() => {
              if (
                !windowCache.latestEnd ||
                context.isFetching ||
                adminCommandFrozen
              )
                return;
              const nextStart = format(
                addDays(parseISO(windowCache.latestEnd), 1),
                'yyyy-MM-dd',
              );
              setBookingWindowStart(nextStart < today ? today : nextStart);
            }}
          />
        </section>
        <section className="flex w-full flex-col gap-3 p-8 lg:w-[355px]">
          {availabilityRefreshFailed ? (
            <Alert severity="error">
              Unable to load more availability.
              <Button
                variant="text"
                onClick={() =>
                  selectionFailed || selectionQuery.isError
                    ? selectCoachingDate(selectedDate)
                    : context.refetch()
                }
              >
                Retry availability
              </Button>
            </Alert>
          ) : null}
          {eligible && slots.length > 0 && (
            <MentoCalTimeList
              heading={mentoCalBookingDayHeading(displayedDate)}
              // Unlike the member scheduler, this card is not height-capped by
              // its layout, so the scroller needs an explicit cap. It is
              // deliberately unprefixed: a breakpoint-qualified cap would leave
              // the list unbounded below 768px, which is the complaint this
              // ticket exists to fix, just at a narrower viewport.
              scrollClassName="max-h-80"
            >
              {slots.map((slot) => (
                <MentoCalTimeSlotButton
                  key={slot.startTime}
                  disabled={
                    adminCommandFrozen ||
                    selectionPending ||
                    selectionFailed ||
                    selectionQuery.isFetching
                  }
                  label={slot.label}
                  selected={slot.startTime === displayedStart}
                  onClick={() => {
                    setIdempotencyKey(window.crypto.randomUUID());
                    setConfirmedConflictFingerprint('');
                    selectedSlotSnapshot.current = JSON.stringify(
                      selectedCommandDay?.slots.find(
                        (entry) => entry.startAt === slot.startTime,
                      ) ?? null,
                    );
                    setSelectedStart(slot.startTime);
                  }}
                />
              ))}
            </MentoCalTimeList>
          )}
          {!eligible && (
            <Alert severity="warning">
              Booking unavailable
              {eligibilityReasons.length > 0
                ? `: ${eligibilityReasons.join(', ')}`
                : '.'}
            </Alert>
          )}
          {selectedConflict && (
            <Alert severity="warning">
              This time conflicts with{' '}
              {selectedConflict.conflictingOccurrenceCount} of{' '}
              {selectedConflict.checkedOccurrenceCount} coaching occurrences on{' '}
              {selectedConflict.calendars
                .map((calendar) => calendar.calendarSummary)
                .join(', ')}
              .
              {conflictOverrideRequired
                ? ' Confirm before booking anyway.'
                : ''}
            </Alert>
          )}
          {actorIsMember && (
            <Alert severity="error">
              An admin cannot book an Appointment where they are the selected
              member.
            </Alert>
          )}
          {lifecycle.record?.phase === 'ambiguous' && (
            <Alert severity="warning">
              The previous admin booking outcome is unknown. Retry replays the
              exact saved request.
            </Alert>
          )}
          {lifecycle.terminalHydrationForbidden && (
            <Alert severity="warning">
              {PLATFORM_ADMIN_TERMINAL_HYDRATION_FORBIDDEN_MESSAGE}
            </Alert>
          )}
          {!lifecycle.terminalHydrationForbidden &&
            lifecycle.record?.phase === 'pending' && (
              <Alert severity="info">Admin booking is pending.</Alert>
            )}
          {lifecycle.record?.phase === 'rejected' && (
            <Alert severity="error">{lifecycle.record.rejection.message}</Alert>
          )}
          {lifecycle.record?.phase === 'needs_reconciliation' && (
            <Alert severity="warning">
              {PLATFORM_ADMIN_MANUAL_REVIEW_MESSAGE}
            </Alert>
          )}
          {Boolean(lifecycle.error) &&
            lifecycle.record?.phase !== 'ambiguous' && (
              <Alert severity="error">Unable to book this Appointment.</Alert>
            )}
          <Button
            disabled={
              !displayedStart ||
              (!lifecycle.record &&
                (!displayedStartAvailable ||
                  displayedDate < today ||
                  !selectedCommandDay?.enabled)) ||
              newBookingIneligible ||
              actorIsMember ||
              (!lifecycle.record &&
                (availabilityRefreshFailed ||
                  context.isFetching ||
                  selectionQuery.isFetching ||
                  selectionPending ||
                  (kind === 'coaching' &&
                    (!selectionContext ||
                      windowCache.days[selectedDate]?.owner !==
                        selectionContext.bookingWindow?.startDate)))) ||
              lifecycle.record?.phase === 'submitting' ||
              lifecycle.record?.phase === 'pending' ||
              lifecycle.record?.phase === 'needs_reconciliation' ||
              lifecycle.record?.phase === 'rejected'
            }
            variant="contained"
            onClick={() => {
              if (
                lifecycle.record?.phase !== 'ambiguous' &&
                conflictOverrideRequired &&
                !conflictConfirmed
              ) {
                setConfirmedConflictFingerprint(selectedConflictFingerprint);
                return;
              }
              const action =
                lifecycle.record?.phase === 'ambiguous'
                  ? lifecycle.resume
                  : lifecycle.submit;
              action().catch(() => undefined);
            }}
          >
            {bookingButtonLabel}
          </Button>
          {lifecycle.record?.phase === 'rejected' && (
            <Button
              variant="text"
              onClick={() => {
                lifecycle
                  .clear()
                  .then((cleared) => {
                    if (!cleared) return;
                    setIdempotencyKey(window.crypto.randomUUID());
                    setSelectedStart('');
                  })
                  .catch(() => undefined);
              }}
            >
              Start new attempt
            </Button>
          )}
        </section>
      </Paper>
    </main>
  );
};

const PlatformAdminBookingPage: FC<PlatformAdminBookingPageProps> = ({
  bookingType,
  coachId,
  kind,
  lifecycleOptions,
  memberId,
  onDone,
}) => (
  <PlatformAdminBookingForm
    key={`${coachId}:${memberId}:${bookingType ?? kind}`}
    bookingType={bookingType}
    coachId={coachId}
    kind={kind}
    lifecycleOptions={lifecycleOptions}
    memberId={memberId}
    onDone={onDone}
  />
);

export default PlatformAdminBookingPage;
